import { Caret } from '@mobile/components/kartoy/caret'
import { KeyboardInput } from '@mobile/components/kartoy/keyboard-input'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import { requestOtp, selectedIsAuthorized } from '@shared-core/features/auth'
import { createUserPhone } from '@shared-core/features/backend'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { PhoneEntryMode, ScreensList, useNavigation } from '@shared-core/navigation'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import {
  type CountryCode,
  formatNationalPhoneInput,
  getInternationalPhoneFromNationalInput,
  getPhoneCallingPrefix,
  getPhoneDigits,
  getPhonePlaceholder,
  isValidPhone,
} from '@shared-core/utils/phone'
import { flags } from '@shared-images/flags'
import { AppPhoneCountryPicker } from '@shared-ui/components/app/phone-country-picker'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { Header } from '@shared-ui/components/ui/header'
import { ScreenWrapperWithoutScroll } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { GText } from '@shared-ui/components/ui/text'
import { useEffect, useState } from 'react'
import { Image, Keyboard, Platform, Pressable } from 'react-native'
import Animated, { FadeInDown } from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

export function ScreenEntryPhone() {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const navigation = useNavigation()
  const dispatch = useAppDispatch()
  const isAuthorized = useAppSelector(selectedIsAuthorized)

  const [digits, setDigits] = useState('')
  const [countryCode, setCountryCode] = useState<CountryCode>('ru')
  const [isPickerVisible, setIsPickerVisible] = useState(false)
  const placeholder = getPhonePlaceholder(countryCode) || t('placeholder')
  const isComplete = digits.length >= getPhoneDigits(placeholder, 0).length
  const internationalPhone = getInternationalPhoneFromNationalInput(digits, countryCode)
  const isValid = isComplete && isValidPhone(internationalPhone, countryCode)
  const hasError = isComplete && !isValid

  useEffect(() => {
    if (Platform.OS !== 'web') Keyboard.dismiss()
  }, [])

  // Отправляем номер: для привязки нужен idempotency, для входа достаточно запроса кода.
  const handleSubmit = () => {
    if (!isValid) return

    const mode = isAuthorized ? PhoneEntryMode.BIND : PhoneEntryMode.LOGIN
    const request =
      mode === PhoneEntryMode.BIND
        ? dispatch(createUserPhone(internationalPhone.replace(/\D/g, '')))
        : dispatch(requestOtp({ phone: internationalPhone.replace(/\D/g, '') }))
    void request.unwrap().then((result) => {
      if (!result) return

      navigation.navigate(ScreensList.ENTRY_OTP, {
        phone: internationalPhone,
        mode,
        idempotency: 'idempotency' in result ? result.idempotency : undefined,
        resendAvailableAt: Date.now() + result.retryAfter * 1000,
      })
    })
  }

  return (
    <ScreenWrapperWithoutScroll style={styles.container}>
      <Header isBackButton exitCross={false} />

      <Animated.View entering={FadeInDown.delay(60).duration(420)} style={styles.topBlock}>
        <Animated.View style={styles.titleBlock}>
          <GText fw={600} style={styles.title}>
            {t('title')}
          </GText>
          <GText style={styles.subtitle}>{t('subtitle')}</GText>
        </Animated.View>

        <Animated.View entering={FadeInDown.delay(160).duration(420)} style={styles.fieldBlock}>
          <Animated.View style={styles.inputRow}>
            <Pressable
              style={styles.codeBlock}
              accessibilityRole="button"
              accessibilityLabel={getPhoneCallingPrefix(countryCode)}
              onPress={() => setIsPickerVisible(true)}
            >
              <AppIcon icon="chevron-down" size={16} />
              <Animated.View style={styles.flagWrap}>
                <Image source={flags[countryCode as keyof typeof flags]} style={styles.flagWrap} />
              </Animated.View>
              <GText fw={500} style={styles.code}>
                {getPhoneCallingPrefix(countryCode)}
              </GText>
            </Pressable>

            <Animated.View style={styles.numberRow}>
              {!digits && <Caret height={24} />}
              <GText fw={500} style={digits ? styles.code : styles.placeholder}>
                {digits ? formatNationalPhoneInput(digits, countryCode) : placeholder}
              </GText>
              {!!digits && <Caret height={24} />}
            </Animated.View>
          </Animated.View>
          {hasError && <GText style={styles.error}>{t('error')}</GText>}
        </Animated.View>
      </Animated.View>

      <Animated.View entering={FadeInDown.delay(260).duration(420)} style={styles.action}>
        <PillButton
          title={t('action')}
          height={52}
          variant={isValid ? 'primary' : 'secondary'}
          disabled={!isValid}
          onPress={handleSubmit}
        />
      </Animated.View>

      <AppPhoneCountryPicker
        isVisible={isPickerVisible}
        selectedCode={countryCode}
        onClose={() => setIsPickerVisible(false)}
        onSelect={(country) => {
          setCountryCode(country.code)
          setDigits('')
        }}
      />

      <KeyboardInput
        value={digits}
        isDecimalKeyVisible={false}
        onChange={(next) => setDigits(getPhoneDigits(formatNationalPhoneInput(next, countryCode), 0))}
      />
    </ScreenWrapperWithoutScroll>
  )
}
